import { z } from "zod/v4";

// ---------------------------------------------------------------------------
// A template rule's proposal as DATA, not markdown.
//
// A template rule used to be proposed in free prose — an `h2` "Proposed rule"
// and a bulleted list of `Label: value` lines in an ordinary chat bubble — so
// the one thing a merchant reads before approving had no shape a surface could
// render as anything but markdown. The authoring agent now states the same
// lines as rows through `build_agentic_logic_template_rule_proposal`, and a
// surface renders them as its summary card (the Atlas rail's "Survey summary",
// one label-over-value row per line). The rows are the proposal; nothing is
// parsed back out of prose.
// ---------------------------------------------------------------------------

/** The tool that states a template rule's proposal as rows. */
export const AGENTIC_LOGIC_TEMPLATE_RULE_PROPOSAL_TOOL =
  "build_agentic_logic_template_rule_proposal";

// The other authoring tools a surface recognises in a transcript. web/ai names
// its tool definitions with these, so a rename breaks the build on both sides
// instead of silently leaving a surface reading a name no call carries.

/** The tool that composes a code rule's approval card. */
export const AGENTIC_LOGIC_RULE_PROPOSAL_TOOL =
  "build_agentic_logic_rule_proposal";

/** The tool that validates a template rule's parameters. */
export const AGENTIC_LOGIC_VALIDATE_TEMPLATE_TOOL =
  "validate_agentic_logic_template";

/** The tool that creates the rule row itself. */
export const AGENTIC_LOGIC_CREATE_RULE_TOOL = "create_agentic_logic_rule";

export const AgenticLogicRuleProposalRowSchema = z.object({
  label: z
    .string()
    .trim()
    .min(1)
    .max(60)
    .describe(
      "What the line is about, in merchant words and sentence case — `Collection`, `Order`, `Products shown`, `If nothing qualifies`. No trailing colon."
    ),
  value: z
    .string()
    .trim()
    .min(1)
    .max(240)
    .describe(
      "The line's one value, in merchant words — `Home page`, `Price, low to high`, `3`. A single item: never a list, never markdown."
    ),
});

export type AgenticLogicRuleProposalRow = z.infer<
  typeof AgenticLogicRuleProposalRowSchema
>;

export const AgenticLogicRuleProposalSchema = z.object({
  rows: z
    .array(AgenticLogicRuleProposalRowSchema)
    .min(1)
    .max(16)
    .refine(
      (rows) =>
        new Set(rows.map((row) => row.label.toLowerCase())).size ===
        rows.length,
      { error: "Each proposal row needs its own label" }
    )
    .describe(
      "The proposal, one row per line, in the order the merchant should read it: the rule's name first, then the template, every validated parameter, and all four rule settings."
    ),
});

export type AgenticLogicRuleProposal = z.infer<
  typeof AgenticLogicRuleProposalSchema
>;
